import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../services/apiClient';
import { CampaignRow, emailService } from '../services/emailService';
import { useToast } from '../contexts/ToastContext';
import { DataListTable, type Column } from '../components/crm/DataListTable';
import { ListPageHeader } from '../components/crm/ListPageHeader';

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = await emailService.listCampaigns();
        if (!mounted) return;
        setCampaigns(rows);
      } catch (err) {
        if (!mounted) return;
        const message = err instanceof ApiError ? err.message : 'Failed to load campaigns.';
        setError(message);
        showToast(message, 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showToast]);

  const columns: Column<CampaignRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Campaign',
        mobilePriority: 'primary',
        mobileLabel: 'Campaign',
        render: (campaign) => (
          <Link to={`/campaigns/${campaign.id}`} className="font-medium text-primary hover:underline">
            {campaign.name}
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (c) => c.status,
      },
      {
        id: 'recipients',
        header: 'Recipients',
        mobileLabel: 'Recipients',
        render: (c) => c.total_recipients,
      },
      {
        id: 'sent',
        header: 'Sent',
        mobileLabel: 'Sent',
        render: (c) => c.sent_count,
      },
      {
        id: 'failed',
        header: 'Failed',
        mobileLabel: 'Failed',
        render: (c) => c.failed_count,
      },
      {
        id: 'skipped',
        header: 'Skipped',
        mobileLabel: 'Skipped',
        render: (c) => c.skipped_count,
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (c) => new Date(c.created_at).toLocaleString(),
      },
      {
        id: 'sent_at',
        header: 'Sent at',
        mobileLabel: 'Sent at',
        render: (c) => (c.sent_at ? new Date(c.sent_at).toLocaleString() : '—'),
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Outreach Campaigns"
        subtitle="Bulk email sends from your Leads selection."
        actions={
          <Link to="/leads" className="btn-secondary text-sm px-4 py-2 rounded-xl">
            Back to Leads
          </Link>
        }
      />

      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      <DataListTable
        columns={columns}
        rows={campaigns}
        getRowKey={(c) => c.id}
        loading={loading}
        emptyContent={
          <div className="rounded-xl border border-border p-6 text-sm text-fg-muted">
            No outreach campaigns yet. Select leads from the Leads page and click Send Outreach.
          </div>
        }
      />
    </div>
  );
};

export default Campaigns;
